/**
 * COMPONENT: BedDeferrableGroup
 * Bloc d'informations critiques (passeport / permis / CEAM) avec l'échappatoire
 * du cahier L14 : « s'il ne connaît pas une information, il doit avoir la
 * possibilité de cocher une case comme quoi il s'engage à communiquer ces
 * éléments avant le dd/mm/aaaa ».
 *
 * Cocher la case remplace la saisie : les champs du groupe cessent d'être
 * exigés, l'étape redevient franchissable, mais elle est marquée ⚠ dans la
 * frise et l'élément apparaît dans l'en-tête du BED à chaque retour.
 */

import type { ReactNode } from 'react';
import {
  BED_DEFERRAL_GROUP_LABELS,
  formatDueDateFr,
  type BedDeferralGroup,
} from '../../lib/bed/wizard-model';

interface BedDeferrableGroupProps {
  group: BedDeferralGroup;
  /** Rôle concerné (`pilot` / `copilot` / `common`), pour l'identifiant DOM. */
  role: string;
  /** Report actif : date à laquelle le participant s'engage. */
  deferredUntil: string | null;
  /** Date proposée par défaut (deadline de l'édition). */
  defaultDueDate: string;
  onToggle: (deferred: boolean, dueDate: string) => void;
  disabled?: boolean;
  children: ReactNode;
}

export function BedDeferrableGroup({
  group,
  role,
  deferredUntil,
  defaultDueDate,
  onToggle,
  disabled = false,
  children,
}: BedDeferrableGroupProps) {
  const isDeferred = deferredUntil !== null;
  const checkboxId = `defer-${group}-${role}`;
  // La date d'engagement est celle configurée pour l'édition.
  const dueDateLabel = formatDueDateFr(deferredUntil ?? defaultDueDate);

  return (
    <fieldset
      className={`space-y-3 rounded-lg border p-4 ${
        isDeferred ? 'border-amber-200 bg-amber-50/60' : 'border-zinc-200'
      }`}
    >
      <legend className="px-1 text-sm font-semibold text-zinc-900">
        {BED_DEFERRAL_GROUP_LABELS[group]}
      </legend>

      {!isDeferred && <div className="space-y-4">{children}</div>}

      <div className="space-y-2">
        <div className="flex items-start gap-2">
          <input
            type="checkbox"
            id={checkboxId}
            checked={isDeferred}
            disabled={disabled}
            onChange={(event) => onToggle(event.target.checked, defaultDueDate)}
            className="mt-0.5 h-4 w-4 shrink-0 rounded border-zinc-300 text-amber-600 focus:ring-amber-500"
          />
          <label htmlFor={checkboxId} className="cursor-pointer text-sm text-zinc-700">
            Je n’ai pas cette info à disposition. Je m’engage à la communiquer
            {dueDateLabel && <> avant le {dueDateLabel}</>}.
          </label>
        </div>

        {isDeferred && (
          <p className="pl-6 text-xs text-amber-700">
            Votre bulletin restera « incomplet » jusqu’à réception
            {dueDateLabel && <> — date limite : {dueDateLabel}</>}.
          </p>
        )}
      </div>
    </fieldset>
  );
}
